import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "@ebay/nice-modal-react";

import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Box from "ds/components/Box";
import Tile from "ds/components/Tile";
import { LogoAwsColored } from "components/icons/generated";
import TileCheckboxGrid from "components/TileCheckboxGrid";
import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import { AwsIntegration } from "types/generated";
import { CLOUD_INTEGRATIONS } from "shared/CloudIntegration/types";
import useAttachIntegration from "shared/IntentProject/useAttachIntegration";
import CardForm from "components/AttachCloud/Form/CardForm";
import IntegrationField from "components/AttachCloud/Form/Fields/IntegrationField";
import ReadToggleField from "components/AttachCloud/Form/Fields/ReadToggleField";
import WriteToggleField from "components/AttachCloud/Form/Fields/WriteToggleField";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";

type AttachIntegrationDrawerProps = {
  intentProjectId: string;
  attachableAwsIntegrations: AwsIntegration[];
};

type FormData = {
  integrationId: string;
  read: boolean;
  write: boolean;
  awsRegion: string;
};

const AttachIntegrationDrawer = createDrawer<AttachIntegrationDrawerProps>(
  ({ intentProjectId, attachableAwsIntegrations }) => {
    const drawer = useModal();
    const { onError } = useTypedContext(FlashContext);
    const { attachIntegration } = useAttachIntegration();
    const [cloudProvider, setCloudProvider] = useState<CLOUD_INTEGRATIONS>(CLOUD_INTEGRATIONS.AWS);

    const form = useForm<FormData>({
      defaultValues: {
        integrationId: "",
        read: false,
        write: false,
        awsRegion: "",
      },
      mode: "onChange",
    });

    const {
      handleSubmit,
      formState: { isDirty, isValid, errors },
      register,
    } = form;

    const integrationOptions = useMemo(
      () =>
        attachableAwsIntegrations.map((integration) => ({
          value: integration.id,
          label: integration.name,
        })),
      [attachableAwsIntegrations]
    );

    const onSubmit = async (data: FormData) => {
      try {
        await attachIntegration({
          intentProjectId,
          integrationId: data.integrationId,
          read: data.read,
          write: data.write,
          awsRegion: data.awsRegion,
        });

        drawer.hide();
      } catch (error) {
        onError(error);
      }
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Attach Cloud Integration" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody noPadding>
          <DrawerSection gap="x-large">
            {/* Cloud Provider Selection */}
            <Box direction="column" gap="large">
              <TileCheckboxGrid>
                <Tile
                  selected={cloudProvider === CLOUD_INTEGRATIONS.AWS}
                  title="AWS"
                  icon={LogoAwsColored}
                  onClick={() => setCloudProvider(CLOUD_INTEGRATIONS.AWS)}
                  indicator="radio"
                />
              </TileCheckboxGrid>
            </Box>

            {/* AWS Integration Form */}
            {cloudProvider === CLOUD_INTEGRATIONS.AWS && (
              <FormProvider {...form}>
                <CardForm onSubmit={handleSubmit(onSubmit)}>
                  <IntegrationField options={integrationOptions} />
                  <FormField label="AWS Region" error={errors?.awsRegion?.message} noMargin>
                    {({ ariaInputProps }) => (
                      <Input
                        placeholder="Enter AWS region here"
                        error={!!errors?.awsRegion}
                        {...register("awsRegion", { required: "AWS region is required." })}
                        {...ariaInputProps}
                      />
                    )}
                  </FormField>
                  <ReadToggleField />
                  <WriteToggleField />
                  <Box justify="end">
                    <Button type="submit" variant="contrast" disabled={!isValid}>
                      Attach
                    </Button>
                  </Box>
                </CardForm>
              </FormProvider>
            )}
          </DrawerSection>
        </DrawerBody>
      </DrawerForm>
    );
  }
);

export const showAttachIntegrationDrawer = createDrawerTrigger(AttachIntegrationDrawer);
